import React, { useRef, useState } from 'react';
import { default as MuiTextField } from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Box, Text, Flex } from 'rebass';
import PulseLoader from 'react-spinners/PulseLoader';

const useInputStyles = makeStyles(() => {
  return createStyles({
    root: {
      border: 'solid 1px rgba(0, 0, 0, 0.32)',
      backgroundColor: '#ffffff',
      fontFamily: "'IBM Plex Sans', sans-serif",
      borderRadius: 4,

      '&:hover': {
        backgroundColor: '#fff',
      },
      '&:error': {
        backgroundColor: '#fff',
      },
      '&$disabled': {
        backgroundColor: '#fff',
        borderRadius: 4,
      },
      '&$focused': {
        backgroundColor: '#fff',
        border: 'solid 2px rgba(19, 40, 76, 0.6)',
        boxShadow: '0 0 3px 0 rgba(19, 40, 76, 0.6)',
      },
    },
    adornedEnd: {
      paddingRight: 0,
    },
    input: {
      fontFamily: "'IBM Plex Sans', sans-serif",
      color: '#1c273f',
      letterSpacing: '0.15px',
      fontSize: '16px',
      paddingLeft: '16px',
    },
    focused: {},
    error: {
      border: 'solid 1px #b00020',
      '&$focused': {
        backgroundColor: '#fff',

        border: 'solid 2px #b00020',
        boxShadow: '0 0 3px 0 #b00020',
      },
      '&:hover': {
        backgroundColor: '#fff',
      },
    },
    disabled: {
      opacity: 0.5,
      backgroundColor: '#fff',
      borderRadius: 4,
    },
  });
});

const useInputLabelStyles = makeStyles(() =>
  createStyles({
    root: {
      fontFamily: "'IBM Plex Sans', sans-serif",
      color: 'rgba(76, 87, 97, 0.8)',
      letterSpacing: '0.15px',
      fontSize: '16px',
      paddingLeft: '7px',
      '&$focused': {
        letterSpacing: '0.4px',
        color: 'rgba(76, 87, 97, 0.8)',
      },
      '&$error': {
        color: '#b00020',
      },
    },
    focused: {},
    error: {
      '&$focused': {
        color: '#b00020',
      },
    },
  })
);

const useFormHelperStyles = makeStyles(() =>
  createStyles({
    root: {
      marginTop: '4px',
      paddingLeft: '2px',
      fontFamily: "'IBM Plex Sans', sans-serif",
      color: 'rgba(76, 87, 97, 0.7)',
      letterSpacing: '0.4px',
      '&$error': {
        color: '#b00020',
      },
    },
    error: {
      color: '#b00020',
    },
  })
);

interface TextFieldProps {
  error?: boolean;
  disabled?: boolean;
  endAdornment?: boolean;
  type?: 'password' | 'text' | 'email' | 'number' | 'date';
  label: string;
  buttonLabel?: string;
  buttonBgColor?: string;
  helperText: string | undefined;
  value: string | null;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onFocus?: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  withButton?: boolean;
  buttonType?: string;
  name: string;
  inputComponent?: any;
  autoComplete?: string;
  onSubmitClicked?: (e: React.SyntheticEvent) => void;
  onKeyPress?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  isSubmitButtonDisabled?: boolean;
  isLoading?: boolean;
  inputProps?: any;
  defaultValue?: string;
  id?: string;
  select?: boolean;
  children?: React.ReactNode;
}

export const TextField = ({
  error,
  disabled,
  endAdornment,
  type = 'text',
  label = 'Label',
  buttonLabel = 'Sign Up', // The original button used a hardcoded 'Sign Up' so default to that here
  helperText = 'Helper text',
  value,
  onChange,
  onBlur,
  onFocus,
  withButton,
  name,
  inputComponent,
  autoComplete,
  onSubmitClicked,
  onKeyPress,
  isSubmitButtonDisabled,
  isLoading,
  inputProps,
  defaultValue,
  buttonBgColor,
  buttonType,
  id,
  select,
  children,
}: TextFieldProps) => {
  const inputClasses = useInputStyles({ withButton });
  const inputLabelClasses = useInputLabelStyles();
  const formHelperClasses = useFormHelperStyles();
  const inputRef = useRef();
  const [showPassword, setPasswordVisible] = useState(type !== 'password');

  return (
    <Box width="100%">
      <Box css={{ zIndex: 100 }}>
        <MuiTextField
          select={select}
          onKeyPress={onKeyPress}
          name={name}
          defaultValue={defaultValue}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          type={
            showPassword ? (type === 'password' ? 'text' : type) : 'password'
          }
          error={error}
          disabled={disabled}
          fullWidth
          InputProps={{
            classes: inputClasses,
            disableUnderline: true,
            endAdornment:
              endAdornment || type === 'password' ? (
                <ShowHidePassword
                  showPassword={showPassword}
                  setPasswordVisible={setPasswordVisible}
                />
              ) : (
                withButton && (
                  <SubmitButton
                    disabled={isSubmitButtonDisabled}
                    label={buttonLabel}
                    onClick={e => onSubmitClicked && onSubmitClicked(e)}
                    isLoading={isLoading}
                    bgColor={buttonBgColor}
                    buttonType={buttonType}
                  />
                )
              ),
            inputComponent,
            ...inputProps,
          }}
          InputLabelProps={{
            classes: inputLabelClasses,
          }}
          label={label}
          variant="filled"
          inputRef={inputRef}
          helperText={error && helperText}
          FormHelperTextProps={{ classes: formHelperClasses }}
          autoComplete={autoComplete}
          id={id}
        >
          {children}
        </MuiTextField>
      </Box>
    </Box>
  );
};

interface SubmitButtonProps {
  disabled: boolean | undefined;
  label?: string;
  onClick: (e: React.SyntheticEvent) => void;
  isLoading?: boolean;
  bgColor?: string;
  buttonType?: string;
}

const SubmitButton = ({
  disabled,
  onClick,
  isLoading,
  label,
  bgColor = 'blue',
  buttonType,
}: SubmitButtonProps) => {
  const [isMouseDown, setMouseDown] = useState(false);
  const [isHover, setHover] = useState(false);
  return (
    <InputAdornment position="end" style={{ marginLeft: 0 }}>
      <Flex
        onClick={e => onClick && !disabled && onClick(e)}
        onMouseDown={() => !disabled && setMouseDown(!isMouseDown)}
        onMouseUp={() => !disabled && setMouseDown(!isMouseDown)}
        onMouseEnter={() => !disabled && setHover(!isHover)}
        onMouseLeave={() => !disabled && setHover(!isHover)}
        width="103px"
        height="56px"
        bg={
          disabled
            ? 'rgba(0, 0, 0, 0.12)'
            : isMouseDown || isHover
            ? 'rgba(19, 40, 76, 0.92)'
            : bgColor
        }
        justifyContent="center"
        alignItems="center"
        css={{
          cursor: 'pointer',
          borderTopRightRadius: 2.5,
          borderBottomRightRadius: 2.5,
          border: 0,
        }}
        as="button"
        type={buttonType}
      >
        <Text
          fontFamily="label"
          fontSize="16px"
          fontWeight="bold"
          letterSpacing="1px"
          color={disabled ? 'rgba(0, 0, 0, 0.38)' : 'rgba(255, 255, 255, 0.87)'}
        >
          {isLoading ? (
            <PulseLoader
              sizeUnit={'px'}
              size={8}
              color="rgba(255, 255, 255, 0.6)"
              loading={isLoading}
            />
          ) : (
            label
          )}
        </Text>
      </Flex>
    </InputAdornment>
  );
};

interface ShowHidePasswordProps {
  showPassword: boolean;
  setPasswordVisible: (showPassword: boolean) => void;
}

const ShowHidePassword = ({
  showPassword,
  setPasswordVisible,
}: ShowHidePasswordProps) => (
  <InputAdornment position="end">
    <Box
      pr="17px"
      aria-label="toggle password visibility"
      onClick={() => {
        setPasswordVisible(!showPassword);
      }}
      css={{ cursor: 'pointer' }}
    >
      <Text
        fontFamily="'IBM Plex Sans', sans-serif"
        fontSize="10px"
        color="#4c5761"
      >
        {showPassword ? 'HIDE' : 'SHOW'}
      </Text>
    </Box>
  </InputAdornment>
);
